import { useContext, useState } from 'react'
import { PhotoContext } from '../context/PhotoContext'
import { cn } from '../lib/cn'
import { initials } from '../lib/format'

interface AvatarProps {
  name: string
  color?: string | undefined
  size?: 'xs' | 'sm' | 'md' | 'lg' | undefined
  ring?: boolean | undefined
  className?: string | undefined
  /** A photo to show instead of initials. Looked up by name when not given. */
  photo?: string | undefined
}

const sizeMap = {
  xs: 'h-6 w-6 text-[10px]',
  sm: 'h-8 w-8 text-xs',
  md: 'h-10 w-10 text-sm',
  lg: 'h-14 w-14 text-lg',
}

export function Avatar({ name, color = '#FC2265', size = 'sm', ring, className, photo }: AvatarProps) {
  const lookup = useContext(PhotoContext)
  const src = photo ?? lookup(name)
  // A photo that fails to load falls back to initials rather than a broken image.
  const [failed, setFailed] = useState<string | null>(null)
  if (src !== undefined && failed !== src) {
    return (
      <img
        src={src}
        alt={name}
        title={name}
        loading="lazy"
        decoding="async"
        onError={() => setFailed(src)}
        className={cn('rounded-full object-cover shrink-0 select-none', sizeMap[size], ring && 'ring-2 ring-white', className)}
      />
    )
  }
  return (
    <div
      title={name}
      className={cn(
        'flex items-center justify-center rounded-full font-semibold text-white shrink-0 select-none',
        sizeMap[size],
        ring && 'ring-2 ring-white',
        className,
      )}
      style={{ backgroundColor: color }}
    >
      {initials(name)}
    </div>
  )
}

export function AvatarStack({ names, colors, max = 4 }: { names: string[]; colors: string[]; max?: number }) {
  const visible = names.slice(0, max)
  const remaining = names.length - visible.length
  return (
    <div className="flex items-center -space-x-2">
      {visible.map((name, i) => (
        <Avatar key={name + i} name={name} color={colors[i]} size="xs" ring />
      ))}
      {remaining > 0 && (
        <div className="flex h-6 w-6 items-center justify-center rounded-full bg-shell-border text-[10px] font-semibold text-shell-text-active ring-2 ring-shell">
          +{remaining}
        </div>
      )}
    </div>
  )
}
